import React, { useMemo, useState, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { useQuery } from '@apollo/client'
import { css, StyleSheet } from 'aphrodite'
import { COLOR_WHITE } from '../../lib/colors'
import GET_ALL_REQUEST from '../../graphql/Requests/queries/GetAllRequests'
import { statusWithIcon } from '../../lib/requestStatus'
import { toast } from 'react-toastify'
import { COLOR_BLACK } from '../../lib/colors'
import ConfirmationDeleteCommandPopup from '../../modal/ConfirmationDeleteCommandPopup'
import {
  IconCalendar,
  IconClock,
  IconMap,
  IconTrash,
} from '../../components/Icon'
import UIButton from '../../components/UI/UIButton'
import LiveContext from '../../components/Context/LiveContext'
import PlayIcon from '../../assets/play-icon.svg'
import config from '../../config'
import UILimitedText from '../../components/UI/UILimitedText'
import { useEffect } from 'react'
import { Link } from 'react-router-dom'

const styles = StyleSheet.create({
  noDataFound: {
    backgroundColor: COLOR_WHITE,
    padding: '20px',
    textAlign: 'center',
  },
  loading: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  underline: {
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
  },
  overlay: {
    width: '100vw',
    height: '100vh',
    position: 'fixed',
    zIndex: '10',
    top: 0,
    left: 0,
    backgroundColor: `${COLOR_BLACK}30`,
  },
  toast: {
    padding: '20px',
    width: 'fit-content',
  },
  container: {
    width: '100%',
    '@media (max-width: 480px)': {
      padding: '20px 0px 0px 0px',
      marginTop: '80px',
    },
  },
  title: {
    letterSpacing: '-0.05em',
    padding: '0 0px 10px 20px',
    fontWeight: 700,
    fontSize: '1.5em',
    lineHeight: '.9em',
    margin: '0px',
  },
  iconButtons: {
    display: 'flex',
    width: '50%',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5%',
  },
  name: {
    letterSpacing: '-0.05em',
    fontWeight: 600,
    fontSize: '1.5em',
    lineHeight: '.9em',
    margin: '5% 0% 5% 0%',
  },
  subtitle: {
    letterSpacing: '-0.05em',
    margin: '0px',
    padding: '0px 0px 20px 20px',
    fontSize: '1em',
    color: '#718096',
  },
  case: {
    background: 'white',
    borderRadius: '13px',
    width: '90%',
    margin: '0 5% 10% 5%',
    padding: '5% 5% 5% 5%',
  },
  contentContainer: {
    display: 'flex',
    padding: '1.5% 0% 1.5% 0%',
  },
  content: {
    fontFamily: 'Helvetica',
    fontStyle: 'normal',
    fontWeight: '400',
    fontSize: '16x',
    lineHeight: '162.02%',
    display: 'flex',
    alignItems: 'center',
    fontFeatureSettings: 'salt on',
    marginLeft: '8px',
  },
  summary: {
    width: '90',
    padding: '5% 0 10% 0',
    letterSpacing: '-0.05em',
    fontSize: '1em',
    color: '#718096',
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: '5%',
    alignItems: 'center',
  },
  icon: {
    height: '24px',
    strokeWidth: '2px',
  },
  button: {
    display: 'flex',
    justifyContent: 'space-evenly',
    padding: '20px 0px',
  },
  buttonCore: {
    backgroundColor: '#61C4CE',
  },
  disabled: {
    backgroundColor: 'rgb(166, 183, 212)',
    cursor: 'not-allowed',
  },
})

const RequestsDashboardMobile = () => {
  const { t } = useTranslation('translation')

  const environment = config.REACT_APP_INFRA

  const { idWatcher, toggleLivePreview, toggleLivePreviewMinimized } =
    useContext(LiveContext)

  const [isToastOpened, setToastOpened] = useState(false)
  const [actualPage, setActualPage] = useState(0)
  const [previousDisabled, setPreviousDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(true)

  const [totalResult, setTotalResult] = useState(0)
  const [resultRow, setResultRow] = useState(0)

  const [pollInterval, setPollInterval] = useState(
    60000 - new Date().getSeconds() * 1000
  )

  const handleBeginLive = async (item) => {
    localStorage.setItem('idRequest', item.id)
    localStorage.setItem(
      'dateTimeRangeLive',
      JSON.stringify(item.datetimeRange)
    )
    toggleLivePreview(true)
    toggleLivePreviewMinimized(false)
  }

  const {
    data: requestList,
    loading,
    refetch,
  } = useQuery(GET_ALL_REQUEST, {
    variables: {
      first: 10,
      offset: actualPage,
      idWatcher,
    },
    pollInterval: pollInterval,
    onCompleted: (data) => {
      setPollInterval(60000 - new Date().getSeconds() * 1000)
      setPreviousDisabled(!data?.requests?.pageInfo?.hasPreviousPage)
      setNextDisabled(!data?.requests?.pageInfo?.hasNextPage)
      setResultRow(data?.requests?.nodes?.length)
      setTotalResult(data?.requests?.totalCount)
    },
    onError: () => {
      toast.error(t('pages.viewerAccount.demand.fetchError'), {
        toastId: 'error',
        position: 'top-center',
        autoClose: 2000,
        hideProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
    },
  })

  // Geocode.setApiKey('AIzaSyDhrH3hl39Yu-vo3Xc3_ddnpL0sdnQANRQ')
  // Geocode.setLanguage('fr')
  // Geocode.setRegion('fr')
  let tbodyData = useMemo(() => {
    const now = new Date()
    if (requestList) {
      return requestList?.requests?.nodes?.map((item) => ({
        id: item.id,
        items: [
          statusWithIcon(item.status, t(item.status)),
          <Link
            style={{
              color: 'black',
              textDecoration: 'none',
            }}
            to={`/account/demand/${item.id}`}
            key={item.key}
          >
            <p className={css(styles.name)}>{item.title}</p>
          </Link>,
          <div className={css(styles.contentContainer)} key={item.key}>
            <IconMap color="black" />
            <div className={css(styles.content)}>{item.requestAddress}</div>
          </div>,
          <div className={css(styles.contentContainer)} key={item.key}>
            <IconClock color="black" />
            <div className={css(styles.content)}>
              {dayjs(item.datetimeRange.start.value).format('HH:mm')}
            </div>
          </div>,
          <div className={css(styles.contentContainer)} key={item.key}>
            <IconCalendar color="black" />
            <div className={css(styles.content)}>
              {dayjs(item.datetimeRange.start.value).format('DD/MM/YYYY')}
            </div>
          </div>,
          <div className={css(styles.summary)} key={item.key}>
            <UILimitedText text={item.summary} maxLines={3}></UILimitedText>
          </div>,
          <div key={item.key} className={css(styles.buttonsContainer)}>
            {environment === 'dev' ? (
              <div
                onClick={() => handleBeginLive(item)}
                className={css(styles.iconButtons)}
              >
                <img
                  src={PlayIcon}
                  alt="play icon"
                  className={css(styles.picto)}
                />
                <div
                  style={{
                    fontWeight: '600',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {t('pages.viewerAccount.demand.beginLive')}
                </div>
              </div>
            ) : (item.status === 'ACCEPTED' || item.status === 'LIVE') &&
              now >= Date.parse(item.datetimeRange?.start?.value) &&
              now <= Date.parse(item.datetimeRange?.end?.value) - 5 * 6000 ? (
              <div
                onClick={() => handleBeginLive(item)}
                className={css(styles.iconButtons)}
              >
                <img
                  src={PlayIcon}
                  alt="play icon"
                  className={css(styles.picto)}
                />
                <div
                  style={{
                    fontWeight: '600',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    marginTop: '2px',
                  }}
                >
                  {t('pages.viewerAccount.demand.beginLive')}
                </div>
              </div>
            ) : (
              <div
                onClick={() =>
                  toast.info(t('pages.viewerAccount.demand.liveNotInTime'))
                }
                className={css(styles.iconButtons)}
                style={{ opacity: '50%' }}
              >
                <img
                  src={PlayIcon}
                  alt="play icon"
                  className={css(styles.picto)}
                />
                <div
                  style={{
                    fontWeight: '600',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }}
                >
                  {t('pages.viewerAccount.demand.beginLive')}
                </div>
              </div>
            )}
            <div
              onClick={() => handleIconTrash(item)}
              className={css(styles.iconButtons)}
            >
              <IconTrash dynamicStyle={styles.icon} color="black"></IconTrash>
              <div style={{ fontWeight: '600' }}>
                {t('common.button.cancel')}
              </div>
            </div>
          </div>,
        ],
      }))
    } else return []
  }, [requestList])

  const toastConfiguration = (id) => {
    return {
      toastId: id,
      position: 'top-center',
      autoClose: false,
      hideProgressBar: true,
      closeOnClick: false,
      pauseOnHover: false,
      draggable: false,
      progress: undefined,
      closeButton: false,
      onOpen: () => setToastOpened(true),
      onClose: () => setToastOpened(false),
      className: css(styles.toast),
    }
  }

  const handleIconTrash = (item) => {
    toast(
      <ConfirmationDeleteCommandPopup
        message={t('pages.viewerAccount.demand.deleteConfirmationMessage')}
        content={t('pages.viewerAccount.demand.deleteCondition')}
        id={item.id}
        handleConfirm={() => {
          handleDeleteDemand(item)
        }}
      />,
      toastConfiguration(item.id)
    )
  }

  useEffect(() => {
    setPreviousDisabled(!requestList?.requests?.pageInfo?.hasPreviousPage)
    setNextDisabled(!requestList?.requests?.pageInfo?.hasNextPage)
  }, [loading])

  const handleDeleteDemand = () => {
    toast.dismiss()
  }

  return (
    <>
      {isToastOpened && <div className={css(styles.overlay)}></div>}
      <div>
        {loading ? (
          <div className={css(styles.loading)}>
            {t('pages.viewerAccount.demand.loading')}
          </div>
        ) : (
          <>
            <div className={css(styles.container)}>
              <p className={css(styles.title)}>
                {t('pages.viewerAccount.demand.title')}
              </p>
              <p className={css(styles.subtitle)}>
                {t('pages.viewerAccount.demand.findDemand')}
              </p>
              {tbodyData.map((item) => {
                return (
                  <>
                    <p className={css(styles.case)} key={item.id}>
                      {item.items.map((itemize) => {
                        return <>{itemize}</>
                      })}
                    </p>
                  </>
                )
              })}
              {tbodyData.length === 0 ? (
                <div className={css(styles.noDataFound)}>
                  {t('pages.viewerAccount.demand.noDataFound')}
                </div>
              ) : (
                <>
                  <div style={{ textAlign: 'center', color: 'gray' }}>
                    {t('resultNumber', { resultRow, totalResult })}
                  </div>
                  <div className={css(styles.button)}>
                    {!previousDisabled ? (
                      <UIButton
                        label={t('common.button.previousPage')}
                        dynamicStyle={styles.buttonCore}
                        onClick={async () => {
                          refetch({
                            first: 10,
                            offset: actualPage - 10,
                            idWatcher,
                          })
                          setActualPage((prev) => prev - 10)
                          window.scrollTo(0, 0)
                        }}
                      ></UIButton>
                    ) : (
                      <UIButton
                        label={t('common.button.previousPage')}
                        disabled={true}
                        dynamicStyle={styles.buttonCore && styles.disabled}
                      ></UIButton>
                    )}
                    {!nextDisabled ? (
                      <UIButton
                        label={t('common.button.nextPage')}
                        dynamicStyle={styles.buttonCore}
                        onClick={async () => {
                          refetch({
                            first: 10,
                            offset: actualPage + 10,
                            idWatcher,
                          })
                          setActualPage((prev) => prev + 10)
                          window.scrollTo(0, 0)
                        }}
                      ></UIButton>
                    ) : (
                      <UIButton
                        label={t('common.button.nextPage')}
                        disabled={true}
                        dynamicStyle={styles.buttonCore && styles.disabled}
                      ></UIButton>
                    )}
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default RequestsDashboardMobile
